import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Spin } from "antd";
import {
  getBuyAndSupplyBuyItemsEnhancedForUser,
  getWalletInfoForUser,
  getAllBuyAndSupplyOrders,
} from "../../network";
import { toastError } from "../../helpers/toasters";

import TabsHolder from "./TabsHolder";
import CombinedModal from "./CombinedModal";
import EmptyState from "./EmptyState";
import Wallet from "./Wallet";
import OrdersTab from "./OrdersTab";
import BuyItemsGrid from "./BuyItemsGrid";

const TAB_PARAM = {
  1: "buy",
  2: "balance",
  3: "orders",
};

const TAB_FROM_PARAM = {
  buy: 1,
  balance: 2,
  orders: 3,
};

const TABS = {
  BUY: 1,
  BALANCE: 2,
  ORDERS: 3,
};

const getTabFromParam = (tabParam) => TAB_FROM_PARAM[tabParam] || TABS.BUY;

const PageSpinner = () => (
  <div className="d-flex justify-content-center align-items-center pt-5">
    <Spin size="large" />
  </div>
);

const BuyAndSupply2026 = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    getTabFromParam(searchParams.get("tab")),
  ); // 1 --> Buy & Supply, 2 --> Balance, 3 --> Orders
  const [data, setData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [renderMe, setRenderMe] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const { dir } = useSelector((state) => state.language);

  const ordersToShow = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (orderStatusFilter === "all") return data;
    return data.filter((order) => order.statusId === Number(orderStatusFilter));
  }, [data, orderStatusFilter]);

  const handleCancelModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const handleSubmitting = () => {
    handleCancelModal();
    setRenderMe(!renderMe);
  };

  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleTabChange = (e) => {
    const nextTab = e.target.value;
    setActiveTab(nextTab);
    setSearchParams({ tab: TAB_PARAM[nextTab] });
    setLoading(true);
    if (nextTab !== TABS.ORDERS) {
      setOrderStatusFilter("all");
    }
  };

  useEffect(() => {
    if (activeTab === 3) {
      getAllBuyAndSupplyOrders(
        (res) => {
          setLoading(false);
          if (res.success) {
            setData(res.data);
          } else {
            toastError(res.message);
          }
        },
        (res) => {
          setLoading(false);
          toastError(res.message);
        },
      );
    } else if (activeTab === 2) {
      getWalletInfoForUser(
        (res) => {
          setLoading(false);
          if (res.success) {
            setData(res.data);
          } else {
            toastError(res.message);
          }
        },
        (res) => {
          setLoading(false);
          toastError(res.message);
        },
      );
    } else {
      getBuyAndSupplyBuyItemsEnhancedForUser(
        (res) => {
          setLoading(false);
          if (res.success) {
            setData(res.data);
          } else {
            toastError(res.message);
          }
        },
        (res) => {
          setLoading(false);
          toastError(res.message);
        },
      );
    }
  }, [activeTab, renderMe, dir]);

  const getColClass = (indx) =>
    `col-lg-6 ${
      indx % 2 === 0
        ? dir === "ltr"
          ? "ps-lg-0"
          : "pe-lg-0"
        : dir === "ltr"
          ? "pe-lg-0"
          : "ps-lg-0"
    }`;

  let content;
  if (loading) {
    content = <PageSpinner />;
  } else if (activeTab === TABS.ORDERS) {
    content = (
      <OrdersTab
        orderStatusFilter={orderStatusFilter}
        onFilterChange={setOrderStatusFilter}
        orders={ordersToShow}
        hasAnyOrders={Array.isArray(data) && data.length > 0}
        getColClass={getColClass}
      />
    );
  } else if (activeTab === TABS.BALANCE) {
    content = (
      <div className="buy-and-supply__balance-holder row m-0">
        <Wallet
          data={data}
          onWithdrawalSuccess={() => setRenderMe((prev) => !prev)}
        />
      </div>
    );
  } else if (!data?.length) {
    content = <EmptyState data="items" />;
  } else {
    content = (
      <BuyItemsGrid
        items={data}
        onOpen={handleOpenModal}
        getColClass={getColClass}
      />
    );
  }

  return (
    <section dir={dir} className="buy-and-supply buy-and-supply-2026">
      <div
        dir={dir}
        className="buy-and-supply__header d-flex flex-column justify-content-end"
      >
        <TabsHolder active={activeTab} onTabChange={handleTabChange} />
      </div>
      <div
        className={`buy-and-supply__items-holder ${
          activeTab === TABS.BALANCE
            ? "buy-and-supply__items-holder--balance"
            : ""
        }`}
      >
        {content}
      </div>
      {isModalOpen && (
        <CombinedModal
          open={isModalOpen}
          onCancel={handleCancelModal}
          selectedItem={selectedItem}
          onSubmit={handleSubmitting}
        />
      )}
    </section>
  );
};

export default BuyAndSupply2026;
